import { defineStore } from 'pinia';
import { ref } from 'vue';
import { reqGetPlugins } from 'src/api/plugins';
import { pluginRegistry } from 'src/plugins/registry';
import { useProfileStore } from 'src/stores/useProfileStore';

export interface PluginData {
  key: string;
  data: Record<string, object>;
  entity_type?: string;
}

export type LoadedPluginInfo = {
  key: string;
  entity_type: string | undefined;
  module: unknown;
};

export const usePluginsStore = defineStore('plugins', () => {
  const enabledPlugins = ref<PluginData[]>([]);
  const loadedPlugins = ref<string[]>([]);
  const isLoaded = ref(false);

  async function fetchAndLoadPlugins() {
    try {
      const plugins = await reqGetPlugins();
      if (Array.isArray(plugins)) {
        enabledPlugins.value = plugins;
      }
    } catch (error) {
      console.error('Error fetching plugins:', error);
    } finally {
      isLoaded.value = true;
    }
  }

  async function loadPlugins(keys: string[]): Promise<LoadedPluginInfo[]> {
    const promises = keys.map(async (id) => {
      const plugin = enabledPlugins.value.find((entry) => entry.key === id);
      if (!plugin) return null;

      const loadFn = pluginRegistry[id];
      if (loadFn) {
        try {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const module: any = await loadFn();

          if (!loadedPlugins.value.includes(id)) {
            console.log('%cplugin', 'background: red; color: white; padding: 4px; border-radius: 7px;', plugin);
            if (typeof module.default?.setup === 'function') {
              await module.default.setup(plugin.data, plugin.entity_type);
            } else if (typeof module.default === 'function') {
              await module.default(plugin.data, plugin.entity_type);
            } else if (typeof module.setup === 'function') {
              await module.setup(plugin.data, plugin.entity_type);
            }

            loadedPlugins.value.push(plugin.key);
            console.log(`Plugin ${plugin.key} loaded.`);
          }

          return { key: id, entity_type: plugin.entity_type, module };
        } catch (error) {
          console.error(`Failed to load plugin ${plugin.key}:`, error);
          return null;
        }
      } else {
        console.warn(`Plugin ${plugin.key} enabled but not found in registry.`);
        return null;
      }
    });
    const results = await Promise.all(promises);
    return results.filter((result): result is LoadedPluginInfo => result !== null);
  }

  function findEntityIdFromType(entity_type: string) {
    const profileStore = useProfileStore();
    console.log('%centityType', 'background: red; color: white; border-radius: 7px; padding: 2px;', entity_type);
    console.log(
      '%cprofileStore.companyId',
      'background: red; color: white; border-radius: 7px; padding: 2px;',
      profileStore.companyId
    );
    console.log(
      '%cprofileStore.userId',
      'background: red; color: white; border-radius: 7px; padding: 2px;',
      profileStore.userId
    );
    switch (entity_type) {
      case 'COMPANY':
        return profileStore.companyId;
      case 'USER':
        return profileStore.userId;
      case 'AGENCY':
        return profileStore.companyId; // TODO: implement agency id
      default:
        return null;
    }
  }

  return {
    enabledPlugins,
    loadedPlugins,
    isLoaded,
    fetchAndLoadPlugins,
    loadPlugins,
    findEntityIdFromType
  };
});
